import * as Schema from 'owa-graph-schema';
import { getOperationAST, OperationDefinitionNode, SelectionSetNode } from 'graphql';
import {
    FetchResult,
    TypePolicy,
    InMemoryCache,
    Observable,
    Operation,
    NextLink,
    ApolloClient,
    NormalizedCacheObject,
} from '@apollo/client';
import type {
    OwaCachePolicyMap,
    LazyTypePolicyFactory,
    LazyFieldPolicyFactory,
    OwaTypePolicy,
} from 'owa-lazy-cache-policy';
import cloneDeep from 'lodash-es/cloneDeep';
import merge from 'lodash-es/merge';

type SampleFieldName = Extract<'sample', keyof Schema.Query>;
type SampleTypeName = Schema.SampleResult['__typename'];

type FieldPolicyCreationInfo = {
    fieldname: string;
    typename: string;
    typePolicy: TypePolicy;
    fieldFactory: LazyFieldPolicyFactory<any, never>;
};

type TypePolicyCreationInfo = {
    typename: string;
    typeFactory: LazyTypePolicyFactory<any>;
};

let initialized = false;
let fieldPolicies = new Map<string, Array<FieldPolicyCreationInfo>>();
let typePolicies = new Map<string, Array<TypePolicyCreationInfo>>();
let removeSamples = true;
const SANITY_CHECK = 100;

export function resetStateForTests() {
    initialized = false;
    fieldPolicies = new Map<string, Array<FieldPolicyCreationInfo>>();
    typePolicies = new Map<string, Array<TypePolicyCreationInfo>>();
    removeSamples = false;
}

export function request(
    client: ApolloClient<NormalizedCacheObject>,
    policies: OwaCachePolicyMap,
    operation: Operation,
    forward: NextLink
): Observable<FetchResult> {
    if (!initialized) {
        populateCreationInfo(policies);
        initialized = true;
    }

    const opNode = getOperationAST(operation.query)!;
    const unloadedPolicyCountBefore = getUnloadedPolicyCount();

    return new Observable(subscriber => {
        // on the outbound side, we need to recursively load all field policies that are in the AST that haven't been loaded
        const pendingFieldPolicies = loadFieldPolicies(client, opNode);
        let completePromise = Promise.resolve();

        return forward(operation).subscribe({
            next: r => {
                // on the inbound side, we need to recursively load all type policies for types that are in the result that haven't been loaded
                var p = pendingFieldPolicies
                    .then(() => loadTypePolicies(client, r))
                    .then(() => {
                        const unloadedPolicyCountAfter = getUnloadedPolicyCount();
                        if (unloadedPolicyCountAfter === unloadedPolicyCountBefore) {
                            // if we didn't actually load any policies for this operation, we can return the result directly
                            return Promise.resolve(r);
                        } else {
                            // if we did load policies, we need to run the operation through the apollo client again to
                            // ensure we use the field policy we just loaded
                            return reExecuteOperation(client, operation);
                        }
                    })
                    .then(r => {
                        subscriber.next?.(r);
                    })
                    .catch(e => subscriber.error?.(e));
                completePromise = Promise.all([completePromise, p]).then();
            },
            error: e => {
                subscriber.error?.(e);
            },
            complete: () => {
                completePromise.then(() => subscriber.complete?.());
            },
        });
    });
}

function getUnloadedPolicyCount() {
    return typePolicies.size + fieldPolicies.size;
}

async function reExecuteOperation(
    client: ApolloClient<NormalizedCacheObject>,
    op: Operation
): Promise<FetchResult> {
    const opNode = getOperationAST(op?.query)!;

    if (opNode.operation === 'query') {
        return client.query({
            query: op.query,
            variables: op.variables,
            context: op.getContext(),
            fetchPolicy: 'cache-first',
        });
    } else {
        return client.mutate({
            mutation: op.query,
            variables: op.variables,
            context: op.getContext(),
        });
    }
}

async function loadFieldPolicies(
    client: ApolloClient<NormalizedCacheObject>,
    node: OperationDefinitionNode
): Promise<void> {
    if (fieldPolicies.size > 0) {
        const selections = node.selectionSet;
        const cache = client.cache as InMemoryCache;
        const policies = loadFieldPoliciesImpl(selections, 0).map(pi => {
            var promise = pi.fieldFactory();
            return {
                ...pi,
                promise,
            };
        });

        const resolvedPolicies = await Promise.all(policies.map(p => p.promise));
        for (let i = 0; i < policies.length; ++i) {
            const policyInfo = policies[i];
            const policy = cloneDeep(policyInfo.typePolicy);
            policy.fields = {
                [policyInfo.fieldname]: resolvedPolicies[i],
            };
            cache.policies.addTypePolicies({
                [policyInfo.typename]: policy,
            });

            // delete so we only load it once
            fieldPolicies.delete(policyInfo.fieldname);
        }
    }
}

function loadFieldPoliciesImpl(
    ssn: SelectionSetNode,
    depth: number
): Array<FieldPolicyCreationInfo> {
    if (depth > SANITY_CHECK) {
        throw Error('Query too complex');
    }

    let policiesToLoad = new Array<FieldPolicyCreationInfo>();

    for (const selection of ssn.selections) {
        if (selection.kind == 'Field') {
            const fp = fieldPolicies.get(selection.name.value);
            if (fp) {
                policiesToLoad = policiesToLoad.concat(fp);
            }

            if (selection.selectionSet) {
                policiesToLoad = policiesToLoad.concat(
                    loadFieldPoliciesImpl(selection.selectionSet, depth + 1)
                );
            }
        }
    }

    return policiesToLoad;
}

async function loadTypePolicies(
    client: ApolloClient<NormalizedCacheObject>,
    result: FetchResult
): Promise<void> {
    if (typePolicies.size > 0) {
        await loadTypePoliciesImpl(client, result?.data || {}, 0);
    }
}

async function loadTypePoliciesImpl(
    client: ApolloClient<NormalizedCacheObject>,
    o: Record<string, any>,
    depth: number
): Promise<void> {
    if (depth > SANITY_CHECK) {
        throw Error('Object too complex');
    }
    if (o) {
        for (var key of Object.keys(o)) {
            const val = o[key];
            if (val) {
                if (key === '__typename' && typeof val === 'string') {
                    await loadPolicy(client, val);
                } else if (Array.isArray(val)) {
                    val.forEach(v => {
                        if (v && typeof v === 'object') {
                            loadTypePoliciesImpl(client, v, depth + 1);
                        }
                    });
                } else if (typeof val === 'object') {
                    loadTypePoliciesImpl(client, val, depth + 1);
                }
            }
        }
    }
}

async function loadPolicy(
    client: ApolloClient<NormalizedCacheObject>,
    typename: string
): Promise<void> {
    const factories = typePolicies.get(typename);

    if (factories) {
        const cache = client.cache as InMemoryCache;
        const promises: Array<Promise<OwaTypePolicy<any>>> = [];

        for (var i = 0; i < factories.length; ++i) {
            promises.push(factories[i].typeFactory());
        }

        var resolved = await Promise.all(promises);
        var combinedPolicy: TypePolicy = resolved.reduce((p, c) => merge(p, c), {});
        cache.policies.addTypePolicies({ [typename]: combinedPolicy });

        // delete so we only load the policy once
        typePolicies.delete(typename);
    }
}

function populateCreationInfo(policies: OwaCachePolicyMap) {
    for (let typename of Object.keys(policies)) {
        const val = policies[typename];

        if (typeof val === 'function') {
            // if the value is a function, then it's a lazyTypePolicyFactory
            const typeFactory = val as LazyTypePolicyFactory<any>;
            const tpInfo: TypePolicyCreationInfo = {
                typename,
                typeFactory,
            };
            addPolicy(typePolicies, typename, tpInfo);
        } else {
            // otherwise, it's a TypePolicy where each property in the 'fields' has a lazyFieldPolicyFactory
            const tp = val as TypePolicy;
            const fields = tp.fields;
            if (fields) {
                Object.keys(fields).forEach(fieldname => {
                    const tpCopy = cloneDeep(tp);
                    const fieldFactory = fields[fieldname] as LazyFieldPolicyFactory<any, never>;
                    tpCopy.fields = {};

                    const fpInfo: FieldPolicyCreationInfo = {
                        typePolicy: tpCopy,
                        fieldname,
                        typename,
                        fieldFactory,
                    };
                    addPolicy(fieldPolicies, fieldname, fpInfo);
                });
            }
        }
    }

    // remove the sample values so they don't change runtime behavior
    if (removeSamples) {
        const sampleFieldName: SampleFieldName = 'sample';
        fieldPolicies.delete(sampleFieldName);

        const sampleTypeName: SampleTypeName = 'SampleResult';
        typePolicies.delete(sampleTypeName);
    }
}

function addPolicy<T>(map: Map<string, Array<T>>, key: string, val: T) {
    let infos: Array<T> | null = map.get(key) || null;

    if (!infos) {
        infos = new Array<T>();
        map.set(key, infos);
    }

    infos.push(val);
}
